import { useMemo } from 'react'
import { parse, ParsedQs } from 'qs'
import moment from 'moment'
import { useLocation } from 'react-router-dom'
import { z } from 'zod'

/**
 * Custom hook to extract and parse URL parameters for the Resurrection page
 *
 * @param location - Location object containing search string
 * @param contentfulUrlParams - Optional URL parameters string from Contentful
 * @returns Parsed URL parameters with proper types
 */
// Helper to convert string or string[] to number[]
const toNumArray = (value?: string | string[]): number[] => {
  if (!value) return []
  const str = Array.isArray(value) ? value.join(',') : value
  return str
    .split(',')
    .filter(Boolean)
    .map(n => parseInt(n, 10))
}

// Zod schema for query params with passthrough to keep extra keys
const queryParamsSchema = z
  .object({
    chefBrandIds: z.union([z.string(), z.array(z.string())]).optional(),
    mealIds: z.union([z.string(), z.array(z.string())]).optional(),
    d: z.string().optional(),
    fromLogin: z.string().optional(),
    errorMessage: z.string().optional()
  })
  .passthrough()
  .transform(data => {
    const isExpired = data.d
      ? moment().startOf('day') > moment(data.d).add(4, 'days')
      : false

    return {
      chefBrandIds: toNumArray(data.chefBrandIds),
      mealIds: toNumArray(data.mealIds),
      expiryDate: data.d,
      isExpired,
      isFromLogin: data.fromLogin === 'true',
      errorMessage: data.errorMessage ?? '',
      rawParams: data as ParsedQs
    }
  })

type ParsedQueryParams = z.infer<typeof queryParamsSchema>

// Defaults used when parsing fails
const DEFAULT_QUERY_PARAMS: ParsedQueryParams = {
  chefBrandIds: [],
  mealIds: [],
  expiryDate: undefined,
  isExpired: false,
  isFromLogin: false,
  errorMessage: '',
  rawParams: {}
}

/**
 * Custom hook to extract and parse URL parameters for the Resurrection page
 *
 * @param location - (optional) Location object with `search` string. If omitted, the hook calls useLocation().
 * @param contentfulUrlParams - Optional URL parameters string from Contentful
 * @returns Parsed URL parameters with proper types
 */
export function useResurrectionUrlParams(
  location?: { search: string },
  contentfulUrlParams?: string
) {
  const fallbackLocation = useLocation()

  return useMemo<ParsedQueryParams>(() => {
    const effectiveLocation = location ?? fallbackLocation

    // If Contentful params are provided, use them instead of URL params
    const searchString =
      contentfulUrlParams || effectiveLocation.search.slice(1)

    const queryParams = parse(searchString)

    const parsed = queryParamsSchema.safeParse(queryParams)

    return parsed.success ? parsed.data : DEFAULT_QUERY_PARAMS
  }, [location, fallbackLocation, contentfulUrlParams])
}
